import Queue from "queue-promise";
import Auth from "../Auth";
import { db } from "../Firebase.js";
import Utils from "../Utils";

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const DB_NAME = "sudowrite-document-cache";
const STORE_NAME = "documents";

const memoryCache = new Map();

let dbPromise = null;
function getDB() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };
    });
  }
  return dbPromise;
}

async function getFromIndexedDB(id) {
  try {
    const db = await getDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function saveToIndexedDB(id, data) {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.put({ id, ...data, cachedAt: Date.now() });
  } catch {
    // Ignore IndexedDB errors - memory cache still works
  }
}

class Document {
  constructor({ id, userId, onConversion }) {
    if (!id) {
      throw new Error("Must specify an id");
    }

    this.id = id;
    this.userId = userId;
    this.onConversion = onConversion; // callback for when the document is converted to YDoc

    // Whether the document is initialized with particular document
    this.initialized = false;

    // Local copy of the attrs
    this.content = null;
    this.updatedAt = null;

    this.stale = false;
    this.pov_tense_id = null; // This is the ID of the POVTense associated with this document, if any

    this.queue = new Queue({ concurrent: 1, interval: 1, start: true });
  }

  static async create(title) {
    let result = await Auth.fetchWithIdToken("/api/document/create", {});
    return result.id;
  }

  static async delete(id) {
    let result = await Auth.fetchWithIdToken(`/api/document/delete/${id}`, {});
    return result;
  }

  async init() {
    const cached = await this._getFromCache();
    if (cached) {
      this.content = cached.content;
      this.updatedAt = cached.updatedAt;
      this.selectionIndex = cached.selectionIndex;
      this.previousId = cached.previousId;
      this.previousDocumentsCount = cached.previousDocumentsCount;
      this.povTenseId = cached.povTenseId;
      this.initialized = true;

      this.get().then(() => this._saveToCache());
      return { error: false };
    }

    let result;
    try {
      result = await this.get();
    } catch (err) {
      // Ghost document — partially created entry with no backing content.
      // Treat as an empty document so the UI can still render and allow deletion.
      console.warn(`[Document ${this.id}] Failed to load, treating as ghost document:`, err);
      this.content = null;
      this.initialized = true;
      return { error: false };
    }

    if (result.error) {
      return { error: true, notFound: result.notFound, message: result.message };
    } else {
      this.initialized = true;
      this._saveToCache();
      return { error: false };
    }
  }

  async _getFromCache() {
    const memory = memoryCache.get(this.id);
    if (memory && (Date.now() - memory.cachedAt) < CACHE_TTL_MS) {
      return memory;
    }

    const stored = await getFromIndexedDB(this.id);
    if (stored && (Date.now() - stored.cachedAt) < CACHE_TTL_MS) {
      memoryCache.set(this.id, stored);
      return stored;
    }

    return null;
  }

  _saveToCache() {
    const data = {
      content: this.content,
      updatedAt: this.updatedAt,
      selectionIndex: this.selectionIndex,
      previousId: this.previousId,
      previousDocumentsCount: this.previousDocumentsCount,
      povTenseId: this.povTenseId,
      cachedAt: Date.now(),
    };
    memoryCache.set(this.id, data);
    saveToIndexedDB(this.id, data);
  }

  async get() {
    const promises = [
      db.collection("documents").doc(this.id).get(),
      Auth.fetchWithIdToken(`/api/document/get/${this.id}`, {}),
    ];

    const results = await Promise.allSettled(promises);

    const firestoreResult = results[0];
    const postgresResult = results[1];

    // Handle missing Firestore document (ghost document — record exists in project contents but no actual content)
    const docData = firestoreResult.status === "fulfilled" ? firestoreResult.value.data() : null;
    const postgresData = postgresResult.status === "fulfilled" ? postgresResult.value : null;

    // Only update fields from sources that succeeded — prevents background refresh
    // from overwriting valid cached data with nulls on transient network failures.
    if (firestoreResult.status === "fulfilled") {
      this.content = docData?.content ?? null;
    }
    if (postgresResult.status === "fulfilled") {
      this.updatedAt = postgresData?.doc?.updatedAt ?? null;
      this.selectionIndex = postgresData?.doc?.selectionIndex ?? null;
      this.previousId = postgresData?.doc?.previousId || null;
      this.previousDocumentsCount = postgresData?.doc?.previousDocumentsCount || 0;
      this.povTenseId = postgresData?.doc?.povTenseId || null;
    }

    this.stale = false;

    return { error: false };
  }

  async update({ povTenseId }) {
    let data = {};
    if (povTenseId !== undefined) {
      data.povTenseId = povTenseId;
    }

    return this.queue.enqueue(async () => {
      let result;
      try {
        result = await Auth.fetchWithIdToken(`/api/document/update/${this.id}`, data);

        return result;
      } catch (e) {
        await this.fetch();
        return result;
      }
    });
  }

  // Listens for metadata updates on the document
  listenForMetadataUpdate() {
    if (!this.initialized) {
      throw new Error("Cannot listen for staleness unless initialized");
    }

    this.stale = false;
    let _this = this;

    // Listen to the doc's metadata to see if it's been updated
    this.unsubscribeStale = db
      .collection("userData")
      .doc(this.userId)
      .collection("documentMetas")
      .doc(this.id)
      .onSnapshot({}, (doc) => {
        // Check if we should ignore stale events (this happens when update is in flight)
        if (_this.ignoreStale) {
          //console.log("Ignoring stale since update in flight");
          return;
        }

        const serverUpdatedAt = Utils.dateFromTimestamp(doc.get("updatedAt"));

        // console.log("Local:", _this.updatedAt);
        // console.log("Server:", serverUpdatedAt);

        // compare epochs
        if (Date.parse(serverUpdatedAt) > Date.parse(_this.updatedAt)) {
          console.log(`[DOC ${this.id}] local updateAt lags behind server`);
          _this.stale = true;
        } else {
          // console.log("local updatedAt is equal to server");
          _this.stale = false;
        }
      });
  }

  // call before disposing of object or when parent component unmounts
  release() {
    if (this.unsubscribeStale) {
      this.unsubscribeStale();
    }
  }

  isStale() {
    return this.stale;
  }

  async saveSelectionIndex(index) {
    if (!this.initialized) {
      throw "You must call init on the Document before saving";
    }

    if (index === undefined || index === null) {
      throw "index is required";
    }

    await db.collection("userData").doc(this.userId).collection("documentMetas").doc(this.id).update({
      selectionIndex: index,
    });

    return true;
  }
}

export default Document;
